import {
  MarketData,
  Settlement,
  SettlementDetails,
} from 'common/src/utils/types';
import { compareSeasonWeekKeys } from 'common/src/utils/seasonWeekOrder';
import { getWeekNumber } from 'common/src/utils/functions';
import { LiquidationData } from './data/data';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

export const groupSize = (size: string): string => {
  size = size.trim();
  size = size.toUpperCase();

  if (size?.endsWith('DD')) {
    const result = size.slice(0, -2);
    if (result === '') {
      console.log('result is empty', size);
      return size;
    }
    return result;
  } else if (size === 'XL') {
    return 'XL';
  } else if (size.endsWith('D') || size.endsWith('L')) {
    const result = size.slice(0, -1);
    if (result === '') {
      console.log('result is empty', size);
      return size;
    }
    return result;
  }

  return size;
};

// Normalize color from long-form to short-form
export const normalizeColor = (color: string): string => {
  if (!color) return 'L'; // Default to Light

  const normalized = color.trim().toUpperCase();

  // Handle long-form color names
  if (normalized === 'LIGHT') return 'L';
  if (normalized === 'DARK DARK' || normalized === 'DARKDARK') return 'DD';
  if (normalized === 'DARK') return 'D';

  // Handle short-form color codes
  if (normalized === 'L' || normalized === 'D' || normalized === 'DD') {
    return normalized;
  }

  // Default to Light if unrecognized
  return 'L';
};

export const groupSizeAndColor = (
  size: string
): { color: string; size: string } => {
  size = size.trim();
  size = size.toUpperCase();

  let color = 'L'; // Light by default
  let result = size;

  if (size?.endsWith('LIGHT')) {
    result = size.slice(0, -5);
    color = 'L';
  } else if (size?.endsWith('DARK')) {
    result = size.slice(0, -4);
    color = 'D';
  } else if (size?.endsWith('DD')) {
    result = size.slice(0, -2);
    color = 'DD';
  } else if (size?.endsWith('D')) {
    result = size.slice(0, -1);
    color = 'D';
  } else if (
    ['LL', 'JL', 'XLL', '2JL', '3JL', '4JL', '5JL', 'GIAL'].includes(size)
  ) {
    result = size.slice(0, -1);
  } else if (size === 'MIX') {
    result = 'MIX';
    color = 'MIX';
  }

  if (result === '') {
    console.log('result is empty', size);
    result = size;
  }

  // Remove leading zeros from the size part (e.g., 02J -> 2J, 003 -> 3)
  // Only strip zeros when followed by a digit to avoid altering non-numeric labels
  result = result.replace(/^0+(?=\d)/, '');

  return {
    color,
    size: result,
  };
};

// New function specifically for Excel processing that handles both short and long-form colors
export const splitSizeAndColorFromExcel = (
  sizeColorCombined: string
): { color: string; size: string } => {
  sizeColorCombined = sizeColorCombined.trim();
  sizeColorCombined = sizeColorCombined.toUpperCase();

  let color = 'L'; // Light by default
  let result = sizeColorCombined;

  // Check if this contains a long-form color name
  if (
    sizeColorCombined.includes('DARK DARK') ||
    sizeColorCombined.includes('DARKDARK')
  ) {
    color = 'DD';
    // Remove the color part to get the size
    result = sizeColorCombined
      .replace('DARK DARK', '')
      .replace('DARKDARK', '')
      .trim();
  } else if (sizeColorCombined.includes('DARK')) {
    color = 'D';
    result = sizeColorCombined.replace('DARK', '').trim();
  } else if (sizeColorCombined.includes('LIGHT')) {
    color = 'L';
    result = sizeColorCombined.replace('LIGHT', '').trim();
  }
  // Check if it ends with short-form color codes (DD, D, L)
  else if (sizeColorCombined.endsWith('DD')) {
    result = sizeColorCombined.slice(0, -2);
    color = 'DD';
  } else if (sizeColorCombined.endsWith('D')) {
    result = sizeColorCombined.slice(0, -1);
    color = 'D';
  } else if (sizeColorCombined.endsWith('L')) {
    result = sizeColorCombined.slice(0, -1);
    color = 'L';
  }

  if (result === '') {
    console.log('result is empty', sizeColorCombined);
    result = sizeColorCombined;
  }

  return {
    color,
    size: result,
  };
};

export const longToShortSeason = (season: string) => {
  const [startYear, endYear] = season.split('-');
  return `${startYear.slice(-2)}-${endYear.slice(-2)}`;
};

export const calculateDailyAverage = (data: MarketData[]) => {
  const dailyData: { [key: string]: number[] } = {};

  data.forEach((item) => {
    let dateKey: string;

    const dateStr = String(item.date);

    // Check if it's a full ISO datetime string with timezone
    if (dateStr.includes('T')) {
      // It's an ISO string, extract just the date part
      dateKey = dateStr.split('T')[0];
    } else if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // It's already in YYYY-MM-DD format
      dateKey = dateStr;
    } else {
      // Parse as local date to avoid timezone issues
      const [year, month, day] = dateStr.split('-');
      if (year && month && day) {
        dateKey = dateStr;
      } else {
        // Fallback: parse as date object, but use local parsing
        const tempDate = new Date(dateStr + 'T12:00:00');
        const year = tempDate.getFullYear();
        const month = String(tempDate.getMonth() + 1).padStart(2, '0');
        const day = String(tempDate.getDate()).padStart(2, '0');
        dateKey = `${year}-${month}-${day}`;
      }
    }

    const price = item.price_kg;

    if (!dailyData[dateKey]) {
      dailyData[dateKey] = [];
    }

    if (price && price > 0) {
      dailyData[dateKey].push(+price);
    }
  });

  const result = Object.entries(dailyData)
    .map(([date, prices]) => {
      const [year, month, day] = date.split('-').map(Number);

      // Calculate Median
      const sorted = prices.sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      const median = sorted.length % 2 === 0
        ? (sorted[mid - 1] + sorted[mid]) / 2
        : sorted[mid];

      return {
        x: Date.UTC(year, month - 1, day, 12),
        y: parseFloat(median.toFixed(2)),
        count: prices.length, // Return count for weighted calculations downstream
      };
    })
    .sort((a, b) => a.x - b.x);

  return result;
};

export const generateDateRange = (start: string, end: string) => {
  const dates = [];

  // Parse dates as local dates to avoid timezone issues
  const [startYear, startMonth, startDay] = start.split('-').map(Number);
  const [endYear, endMonth, endDay] = end.split('-').map(Number);

  let currentDate = new Date(startYear, startMonth - 1, startDay);
  const endDate = new Date(endYear, endMonth - 1, endDay);

  while (currentDate <= endDate) {
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const day = String(currentDate.getDate()).padStart(2, '0');
    dates.push(`${year}-${month}-${day}`);

    currentDate.setDate(currentDate.getDate() + 1);
  }

  return dates;
};

export const calculateWeeklyAverage = (data: MarketData[]) => {
  const weeklyData: { [key: string]: number[] } = {};

  data.forEach((item) => {
    let dateValue: Date;
    const dateStr = String(item.date);

    // Check if it's a full ISO datetime string with timezone
    if (dateStr.includes('T')) {
      const datePart = dateStr.split('T')[0];
      const [year, month, day] = datePart.split('-').map(Number);
      dateValue = new Date(year, month - 1, day);
    } else if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateStr.split('-').map(Number);
      dateValue = new Date(year, month - 1, day);
    } else {
      // Fallback
      dateValue = new Date(dateStr);
    }

    // Get week number
    const weekNum = getWeekFromDate(dateValue);
    const weekKey = `W${weekNum}`;

    const price = item.price_kg;

    if (!weeklyData[weekKey]) {
      weeklyData[weekKey] = [];
    }

    if (price && price > 0) {
      weeklyData[weekKey].push(+price);
    }
  });

  const result = Object.entries(weeklyData)
    .map(([week, prices]) => {
      // Calculate Median
      const sorted = prices.sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      const median = sorted.length % 2 === 0
        ? (sorted[mid - 1] + sorted[mid]) / 2
        : sorted[mid];

      return {
        x: week,
        y: parseFloat(median.toFixed(2)),
        count: prices.length,
      };
    })
    // Orden de temporada: 27→53 y luego 1→26
    .sort((a, b) => compareSeasonWeekKeys(a.x, b.x));

  return result;
};

export const generateWeekRange = (start: string, end: string) => {
  const weeks = new Set<string>();

  // Parse dates as local dates
  const [startYear, startMonth, startDay] = start.split('-').map(Number);
  const [endYear, endMonth, endDay] = end.split('-').map(Number);

  let currentDate = new Date(startYear, startMonth - 1, startDay);
  const endDate = new Date(endYear, endMonth - 1, endDay);

  while (currentDate <= endDate) {
    const weekNum = getWeekFromDate(currentDate);
    weeks.add(`W${weekNum}`);
    currentDate.setDate(currentDate.getDate() + 1);
  }

  // Orden de temporada: 27→53 y luego 1→26
  return Array.from(weeks).sort(compareSeasonWeekKeys);
};

export function calculateFOB(liquidationData: LiquidationData) {
  const { boxes, costs } = liquidationData;

  // Calculate total value of boxes
  const totalBoxValue = boxes.reduce((sum, box) => {
    return sum + box.quantity * box.pricePerBox;
  }, 0);

  // Add relevant costs
  const additionalCosts =
    costs.marketEntrance + costs.logistics + costs.marketCosts;

  // Subtract sea freight
  const seaFreight = costs.seaFreight;

  // Calculate FOB
  const fob = totalBoxValue + additionalCosts - seaFreight;

  return fob;
}

export const calculateFOBSettlement = (settlement: Settlement) => {
  const details = settlement.settlement_details || [];
  const hasCompleteDetailFob =
    details.length > 0 &&
    details.every(
      (detail) =>
        detail.usd_total_fob !== null &&
        detail.usd_total_fob !== undefined &&
        Number.isFinite(Number(detail.usd_total_fob))
    );

  if (hasCompleteDetailFob) {
    return details.reduce(
      (total, detail) => total + Number(detail.usd_total_fob),
      0
    );
  }

  const localTotal =
    (settlement.total_revenue_local || 0) -
    (settlement.total_tax_local || 0) -
    (settlement.total_other_costs_local || 0) -
    (settlement.total_repacking_costs_local || 0) -
    (settlement.total_commission_local || 0);

  const usdTotal = settlement.exchange_rate
    ? localTotal / settlement.exchange_rate
    : 0;

  return usdTotal - (settlement.total_freight_usd || 0);
};

export const formatMoney = (amount: number): string => {
  return amount.toLocaleString('es-CL', {
    style: 'currency',
    currency: 'CLP',
  });
};

export const formatMoneyUSD = (
  amount: number,
  options?: { decimals?: 0 | 1 | 2; hideIcon?: boolean }
): string => {
  const decimals = options?.decimals ?? (amount % 1 !== 0 ? 1 : 0);
  let formatted = amount
    .toLocaleString('es-CL', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      useGrouping: true,
    })
    .replace('US', ''); // Removemos el "US" del formato

  if (options?.hideIcon) {
    formatted = formatted.replace('$', '');
  }

  return formatted;
};

export const formatNumber = (
  number: number,
  options?: { decimals?: 0 | 1 | 2; compact?: boolean }
): string => {
  const decimals = options?.decimals ?? (number % 1 === 0 ? 0 : 1);

  if (options?.compact) {
    const abs = Math.abs(number);
    if (abs >= 1_000_000_000) {
      const val = number / 1_000_000_000;
      return `${val.toFixed(val % 1 === 0 ? 0 : Math.min(decimals, 1))}B`;
    }
    if (abs >= 1_000_000) {
      const val = number / 1_000_000;
      // Use MM for millones per request
      return `${val.toFixed(val % 1 === 0 ? 0 : Math.min(decimals, 1))}MM`;
    }
    if (abs >= 1_000) {
      const val = number / 1_000;
      return `${val.toFixed(val % 1 === 0 ? 0 : Math.min(decimals, 1))}k`;
    }
    return number.toLocaleString('es-CL', {
      useGrouping: true,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }

  return number.toLocaleString('es-CL', {
    useGrouping: true,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

export const formatMoneyRMB = (
  amount: number,
  options?: { decimals?: 0 | 1 | 2; hideIcon?: boolean; currency?: string }
): string => {
  const decimals = options?.decimals ?? (amount % 1 !== 0 ? 1 : 0);
  let formatted = amount.toLocaleString('es-CL', {
    style: 'currency',
    currency: options?.currency || 'RMB',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: true,
  });
  // .replace('RMB', '¥');

  if (options?.hideIcon) {
    formatted = formatted.replace('¥', '');
  }

  return formatted;
};

export const formatMoneyNoCurrency = (
  amount: number,
  options?: { decimals?: 0 | 1 | 2; hideIcon?: boolean; currency?: string }
): string => {
  const decimals = options?.decimals ?? (amount % 1 !== 0 ? 1 : 0);
  let formatted = amount
    .toLocaleString('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })
    .replace('$', '');

  return formatted;
};

export const fromYYYYseasonstoYY = (season: string) => {
  const [startYear, endYear] = season.split('-');
  return `${startYear.slice(-2)}-${endYear.slice(-2)}`;
};

export interface ChartFilters {
  variety: string[];
  caliber: string[];
  color: string[];
  format: string[];
  market: string[];
  label: string[];
  receiver: string[];
  fleet: string[];
  country: string[];
  business_type: string[];
  dateType: 'eta' | 'etd' | 'sale_date';
}

const MISSING_BUSINESS_TYPE = 'Sin Tipo de Negocio';
const MISSING_COLOR_LABELS = new Set(['Sin Color', 'No Color']);
const MISSING_MARKET_LABELS = new Set(['Sin Mercado', 'No Market']);
const MISSING_LABEL_LABELS = new Set(['Sin Etiqueta', 'No Label']);
const MISSING_COUNTRY_LABELS = new Set(['Sin País', 'No Country']);
const MISSING_BUSINESS_TYPE_LABELS = new Set([
  MISSING_BUSINESS_TYPE,
  'No Business Type',
]);

const includesAny = (values: string[] | undefined, labels: Set<string>) =>
  values?.some((value) => labels.has(value)) || false;

type GroupKey =
  | 'variety'
  | 'format'
  | 'caliber'
  | 'color'
  | 'market'
  | 'label'
  | 'receiver'
  | 'fleet'
  | 'country'
  | 'business_type';

export const filterSettlementDetails = (
  detail: SettlementDetails,
  settlement: Settlement,
  filters: ChartFilters
): boolean => {
  const varietyMatch =
    filters?.variety?.includes('all') ||
    filters?.variety?.includes(detail.variety?.toUpperCase() || '');

  const caliberMatch =
    filters?.caliber?.includes('all') ||
    filters?.caliber?.includes(detail.caliber || '');

  const formatMatch =
    filters?.format?.includes('all') ||
    filters?.format?.includes(detail.format || '');

  const colorMatch =
    !filters?.color?.length ||
    filters?.color?.includes('all') ||
    filters?.color?.includes(detail.color || '') ||
    (!detail.color && includesAny(filters?.color, MISSING_COLOR_LABELS));

  const marketMatch =
    filters?.market?.includes('all') ||
    filters?.market?.includes(settlement.market?.name || '') ||
    (!settlement.market?.name &&
      includesAny(filters?.market, MISSING_MARKET_LABELS));

  const labelMatch =
    filters?.label?.includes('all') ||
    filters?.label?.includes(detail.label || '') ||
    (!detail.label && includesAny(filters?.label, MISSING_LABEL_LABELS));

  const receiverMatch =
    filters?.receiver?.includes('all') ||
    filters?.receiver?.includes(settlement.receiver || '');

  // Fleet filter: support both Spanish, English values, and codes
  const shipType = settlement.ship_trip?.ship?.type;
  const fleetMatch =
    filters?.fleet?.length === 0 ||
    filters?.fleet?.includes('all') ||
    (shipType === 'A' && (filters?.fleet?.includes('Aéreo') || filters?.fleet?.includes('AIR') || filters?.fleet?.includes('Air'))) ||
    (shipType === 'T' && (filters?.fleet?.includes('Terrestre') || filters?.fleet?.includes('GROUND') || filters?.fleet?.includes('Ground'))) ||
    ((shipType === 'M' || shipType === 'O') && (filters?.fleet?.includes('Marítimo') || filters?.fleet?.includes('OCEAN') || filters?.fleet?.includes('Ocean'))) ||
    (!shipType && (filters?.fleet?.includes('Marítimo') || filters?.fleet?.includes('OCEAN') || filters?.fleet?.includes('Ocean')));

  const countryMatch =
    !filters?.country?.length ||
    filters?.country?.includes('all') ||
    filters?.country?.includes(settlement.market?.country || '') ||
    (!settlement.market?.country &&
      includesAny(filters?.country, MISSING_COUNTRY_LABELS));

  const businessTypeMatch =
    !filters?.business_type?.length ||
    filters?.business_type?.includes('all') ||
    filters?.business_type?.includes(
      settlement.business_type || MISSING_BUSINESS_TYPE
    ) ||
    (!settlement.business_type &&
      filters?.business_type?.some((type) =>
        MISSING_BUSINESS_TYPE_LABELS.has(type)
      ));

  return (
    varietyMatch &&
    caliberMatch &&
    formatMatch &&
    colorMatch &&
    marketMatch &&
    labelMatch &&
    receiverMatch &&
    fleetMatch &&
    countryMatch &&
    businessTypeMatch
  );
};

export const getGroupKey = (
  detail: SettlementDetails,
  settlement: Settlement,
  groupBy: GroupKey
): string => {
  switch (groupBy) {
    case 'variety':
      return detail.variety?.toUpperCase() || 'Sin Variedad';
    case 'format':
      return detail.format || 'Sin Formato';
    case 'caliber':
      return detail.caliber || 'Sin Calibre';
    case 'color':
      return detail.color || 'Sin Color';
    case 'market':
      return settlement.market?.name || 'Sin Mercado';
    case 'label':
      return detail.label || 'Sin Etiqueta';
    case 'receiver':
      return settlement.receiver || 'Sin Cliente';
    case 'fleet':
      const shipType = settlement.ship_trip?.ship?.type;
      if (shipType === 'A') return 'Aéreo';
      if (shipType === 'T') return 'Terrestre';
      return 'Marítimo'; // M or undefined defaults to Marítimo
    case 'country':
      return settlement.market?.country || 'Sin País';
    case 'business_type':
      return settlement.business_type || MISSING_BUSINESS_TYPE;
  }
};

export const groupAndAggregateData = (
  settlements: Settlement[],
  filters: ChartFilters,
  groupBy: GroupKey,
  viewMode: 'day' | 'week'
) => {
  const groups = {};

  settlements.forEach((settlement) => {
    settlement.settlement_details?.forEach((detail) => {
      if (!filterSettlementDetails(detail, settlement, filters)) return;

      const groupKey = getGroupKey(detail, settlement, groupBy);
      if (!groups[groupKey]) {
        groups[groupKey] = new Map();
      }

      // Determinar qué fecha usar según el filtro dateType
      const getDateByType = () => {
        switch (filters.dateType) {
          case 'eta':
            return settlement.ship_trip?.eta;
          case 'etd':
            return settlement.ship_trip?.etd;
          case 'sale_date':
          default:
            return settlement.date_sold;
        }
      };

      const dateValue = getDateByType();
      if (!dateValue) return; // Skip if date is not available

      const timeKey =
        viewMode === 'day'
          ? new Date(dateValue).getTime()
          : `W${getWeekNumber(dateValue)}`;

      const existing = groups[groupKey].get(timeKey) || {
        totalFob: 0,
        totalKilos: 0,
      };

      groups[groupKey].set(timeKey, {
        totalFob: existing.totalFob + (detail.usd_total_fob || 0),
        totalKilos: existing.totalKilos + (detail.kilos || 0),
      });
    });
  });

  return groups;
};

export const processChartData = (
  groups: Record<
    string,
    Map<string | number, { totalFob: number; totalKilos: number }>
  >,
  viewMode: 'day' | 'week',
  colorFn?: (key: string, index: number) => string,
  valueMode: 'average' | 'total' = 'average',
  multiplier: number = 1
) => {
  // Primero obtenemos todas las fechas únicas y las ordenamos
  const allDates = new Set<string | number>();
  Object.values(groups).forEach((dateMap) => {
    dateMap.forEach((_, key) => allDates.add(key));
  });

  const sortedDates = Array.from(allDates).sort((a, b) => {
    if (viewMode === 'day') {
      return (a as number) - (b as number);
    }

    // Orden de temporada: 27→53 y luego 1→26
    return compareSeasonWeekKeys(a as string, b as string);
  });

  console.log('groups', groups);

  // Ahora creamos las series manteniendo el orden de las fechas
  const series = Object.entries(groups).map(([key, dateMap], index) => {
    const data = sortedDates.map((timeKey) => {
      const value = dateMap.get(timeKey);
      if (!value || value.totalKilos <= 0) {
        return {
          x: timeKey,
          y: null,
          totalFob: 0,
          totalKilos: 0,
        };
      }
      return {
        x: timeKey,
        y: valueMode === 'total' ? value.totalFob : (value.totalFob / value.totalKilos) * multiplier,
        // Keep raw totals on each point so consumers can rank series by FOB
        // regardless of valueMode and compute weighted averages for totals rows.
        totalFob: value.totalFob,
        totalKilos: value.totalKilos,
      };
    });

    return {
      name: key,
      data,
      color: colorFn
        ? colorFn(key, index)
        : `hsl(${index * (360 / Object.keys(groups).length)}, 70%, 50%)`,
    };
  });

  const categories =
    viewMode === 'day'
      ? sortedDates.map((date) =>
          format(date as number, 'dd MMM', { locale: es })
        )
      : (sortedDates as string[]);

  console.log('series', series);
  console.log('categories', categories);

  return { series, categories };
};

// Helper function to parse UTC date as local date (avoid timezone conversion)
const parseUTCDateAsLocal = (utcDateString: string): Date => {
  // Extract just the date part (YYYY-MM-DD) to avoid timezone issues
  const datePart = utcDateString.split('T')[0];
  const [year, month, day] = datePart.split('-').map(Number);
  return new Date(year, month - 1, day); // month is 0-indexed in JS
};

export const formatDate = (date: string | null | undefined, formatString: string = 'dd MMM') => {
  if (!date) return '-';
  try {
    const parsed = parseUTCDateAsLocal(date);
    if (isNaN(parsed.getTime())) return '-';
    return format(parsed, formatString, { locale: es });
  } catch {
    return '-';
  }
};

// Helper function to convert Excel date numbers to JavaScript dates
export const excelDateToJSDate = (excelDate: number): string => {
  // Excel dates are number of days since 1900-01-01
  // Excel has a leap year bug where it thinks 1900 was a leap year
  // So we need to adjust for dates after February 28, 1900

  // Calculate the milliseconds since 1900-01-01
  const msFromExcelDate =
    (excelDate - (excelDate > 59 ? 2 : 1)) * 24 * 60 * 60 * 1000;
  const baseDate = new Date(1900, 0, 1);
  const baseTime = baseDate.getTime();

  // Create a date object with the correct time
  const date = new Date(baseTime + msFromExcelDate);

  // Fix timezone issues by creating a date string in YYYY-MM-DD format
  // This ensures we preserve the exact date regardless of timezone
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  // For time fields, we can include the time portion
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  // Return an ISO string with the local date (not UTC)
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

export const getDaysSinceArrival = (eta: string) => {
  const etaDate = new Date(eta);
  const today = new Date();
  const diffTime = today.getTime() - etaDate.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

// Get week number from date using ISO week calculation
// This function is used consistently across all charts to ensure week numbers match
export const getWeekFromDate = (date: Date): number => {
  const d = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  );
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(
    ((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7
  );
  return weekNo;
};
